import { AxiosError } from 'axios'

import { useQuery, UseQueryOptions } from '@tanstack/react-query'

import { BusinessPackage } from '@/interfaces/billing'

import APIServices from '../../api/APIServices'

export interface BusinessPackagesQueryParams {
  businessId: number
}

// Create a unique, descriptive key for this query
const BUSINESS_PACKAGES_QUERY_KEY = 'business-packages'

/**
 * Fetch invoices from the API
 * Separating API call logic from the hook for better testability
 */
const fetchBusinessPackages = async (): Promise<BusinessPackage[]> => {
  const response = await APIServices.get('/administration/business/packages')
  return response.data
}

const editBusinessPackages = async (
  data: Record<string, unknown>
): Promise<string> => {
  const resp = await APIServices.patch(
    '/administration/business/packages',
    data
  )
  return resp.data
}

/**
 * Custom hook for fetching invoices with filtering
 *
 * @param params - Query parameters for filtering invoices
 * @param options - TanStack Query options for customizing query behavior
 * @returns Query result object containing data, loading states, and utility functions
 */
export const useBusinessPackagesQuery = ({
  params,
  options,
}: {
  params: BusinessPackagesQueryParams
  options?: Partial<UseQueryOptions<BusinessPackage[], AxiosError>>
}) => {
  // Query hook
  const queryResult = useQuery<BusinessPackage[], AxiosError>({
    // Create a structured query key that includes all parameters
    queryKey: [BUSINESS_PACKAGES_QUERY_KEY, params?.businessId],

    // Separate the API call logic from the query configuration
    queryFn: () => fetchBusinessPackages(),

    // Default options that can be overridden
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 60 * 1000, // 2 hours
    enabled: Boolean(params?.businessId),

    // Apply any custom options
    ...options,
  })

  return {
    businessPackages: queryResult.data,
    isLoadingBusinessPackages: queryResult.isLoading,
    isErrorBusinessPackages: queryResult.isError,
    refetchBusinessPackages: queryResult.refetch,
    editBusinessPackages,
  }
}

/**
 * Helper function to get the invoice query key
 * Useful for cache invalidation or prefetching
 */
export const getBusinessPackagesQueryKey = (
  params?: BusinessPackagesQueryParams
) => [BUSINESS_PACKAGES_QUERY_KEY, params?.businessId]
